import { PerpsProvider } from '@kwenta/sdk/types'
import { formatDollars, formatPercent } from '@kwenta/sdk/utils'
import { type FC, memo, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import type { WeiSource } from '@kwenta/wei'
import { TransactionButton } from 'components/Button/Button'
import { DRAG_HANDLE_CLASS_NAME } from 'components/GridLayout'
import { InfoBoxRow } from 'components/InfoBox'
import Pill from 'components/Pill'
import Spacer from 'components/Spacer'
import { Body, NumericValue } from 'components/Text'
import { FlexDivCol, FlexDivRowCentered } from 'components/layout/flex'
import { DEFAULT_FIAT_DECIMALS } from 'constants/defaults'
import { EVENT_NAME } from 'constants/matomoEvents'
import { PROVIDER_CHAIN_ICONS } from 'constants/network'
import { NO_VALUE } from 'constants/placeholder'
import Connector from 'containers/Connector'
import { useAnalytics } from 'hooks/useAnalytics'
import useWindowSize from 'hooks/useWindowSize'
import { DrawerTab, WithdrawTab } from 'sections/accounts/constants'
import { setOpenModal, setOpenModalWithParams } from 'state/app/reducer'
import { selectPerpsProvider } from 'state/futures/common/selectors'
import { selectIsolatedAccountEquity } from 'state/futures/isolatedMargin/selectors'
import {
	selectAccountContext,
	selectIsIsolatedMarginProvider,
	selectTotalUnrealizedPnl,
} from 'state/futures/selectors'
import {
	selectCrossMarginMarginInfo,
	selectMaintenanceMarginRatio,
} from 'state/futures/snxPerpsV3/selectors'
import { ManageModalType } from 'state/futures/types'
import { useAppDispatch, useAppSelector } from 'state/hooks'
import { selectAbstractionWalletEnabled } from 'state/wallet/selectors'
import { providerIsCrossMargin } from 'utils/futures'

import MarginRatioBarChart from '../MarginRatioBar'

const TradeBalance: FC = memo(() => {
	const { t } = useTranslation()
	const dispatch = useAppDispatch()
	const { trackAccountCreation, trackManageAccount, trackTransferDepositFlow, trackLogin } =
		useAnalytics()
	const { isWalletConnected, login } = Connector.useContainer()
	const { deviceType } = useWindowSize()
	const { ratio, riskStatus } = useAppSelector(selectMaintenanceMarginRatio)
	const { accountId } = useAppSelector(selectAccountContext)
	const provider = useAppSelector(selectPerpsProvider)
	const totalUnPnl = useAppSelector(selectTotalUnrealizedPnl)
	const isolatedAccountEquity = useAppSelector(selectIsolatedAccountEquity)
	const isIsolated = useAppSelector(selectIsIsolatedMarginProvider)
	const abstractionWalletEnabled = useAppSelector(selectAbstractionWalletEnabled)
	const { requiredMaintenanceMargin, availableMargin } = useAppSelector(selectCrossMarginMarginInfo)
	const isAccountConnected = useMemo(() => !!accountId, [accountId])

	const handleCreateAccount = useCallback(() => {
		trackAccountCreation(EVENT_NAME.CLICK_CREATE_ACCOUNT)
		dispatch(setOpenModal('acknowledgement_terms'))
	}, [dispatch, trackAccountCreation])

	const { text: actionBtnText, action: handleOnActionBtnClick } = useMemo(() => {
		if (!isWalletConnected) {
			return {
				text: 'futures.market.trade.trade-balance.connect-button',
				action: () => {
					trackLogin(EVENT_NAME.CLICK_LOGIN)
					login()
				},
			}
		}

		if (accountId) {
			if (abstractionWalletEnabled) {
				return {
					text: 'futures.market.trade.trade-balance.transfer-button',
					action: () => {
						trackTransferDepositFlow(EVENT_NAME.CLICK_TRANSFER_DEPOSIT)
						dispatch(
							setOpenModalWithParams({
								type: 'manage_wallet',
								params: { tab: DrawerTab.WITHDRAW, innerTab: WithdrawTab.INTERNAL },
							})
						)
					},
				}
			} else {
				return {
					text: 'futures.market.trade.trade-balance.transfer-button',
					action: () => {
						trackManageAccount(EVENT_NAME.CLICK_MANAGE_ACCOUNT)
						isIsolated
							? dispatch(
									setOpenModalWithParams({
										type: 'futures_deposit_withdraw_isolated_margin',
										params: { tab: ManageModalType.Bridge },
									})
								)
							: dispatch(
									setOpenModalWithParams({
										type: 'manage_wallet',
										params: { tab: DrawerTab.WITHDRAW, innerTab: WithdrawTab.INTERNAL },
									})
								)
					},
				}
			}
		}
		return {
			text: 'futures.market.trade.trade-balance.create-account-button',
			action: handleCreateAccount,
		}
	}, [
		dispatch,
		isWalletConnected,
		accountId,
		isIsolated,
		login,
		abstractionWalletEnabled,
		trackManageAccount,
		trackLogin,
		handleCreateAccount,
		trackTransferDepositFlow,
	])

	const totalUnPnlColor = useMemo(() => {
		const rounded = Number(Number(totalUnPnl).toFixed(DEFAULT_FIAT_DECIMALS))
		return rounded === 0 ? 'primary' : rounded < 0 ? 'negative' : 'positive'
	}, [totalUnPnl])

	const getInfoValue = useCallback(
		(value: WeiSource) => {
			if (!accountId) {
				return NO_VALUE
			}

			return formatDollars(value, { floor: true })
		},
		[accountId]
	)

	return (
		<Container mobile={deviceType === 'mobile'}>
			<FlexDivRowCentered className={DRAG_HANDLE_CLASS_NAME}>
				<Body weight="bold" size="large">
					{providerIsCrossMargin(provider)
						? t('futures.market.trade.margin.perps-v3-account')
						: t('futures.market.trade.margin.perps-v2-account')}
				</Body>
				<TransactionButton
					variant="highlight"
					size="xsmall"
					textTransform="none"
					onClick={handleOnActionBtnClick}
				>
					{t(actionBtnText)}
				</TransactionButton>
			</FlexDivRowCentered>
			<Spacer height={15} />
			{providerIsCrossMargin(provider) ? (
				<>
					<FlexDivRowCentered justifyContent="flex-start" columnGap="4px">
						<StyledPill>
							<IconContainer>{PROVIDER_CHAIN_ICONS[provider]}</IconContainer>
							{t('futures.market.trade.trade-balance.base-network')}
						</StyledPill>
						<StyledPill>{t('futures.market.trade.trade-balance.cross-margin')}</StyledPill>
					</FlexDivRowCentered>
					<Spacer height={15} />
					<FlexDivRowCentered>
						<Body color="secondary">{t('futures.market.trade.margin.margin-ratio')}</Body>
						<FlexDivRowCentered>
							<NumericValue
								weight="bold"
								fontSize={16}
								color={
									!isAccountConnected
										? 'secondary'
										: riskStatus === 'high'
											? 'negative'
											: riskStatus === 'low'
												? 'positive'
												: 'preview'
								}
							>
								{formatPercent(ratio, { maxDecimals: 2 })}
							</NumericValue>
						</FlexDivRowCentered>
					</FlexDivRowCentered>
					<MarginInfoSection>
						<MarginRatioBarRow>
							<MarginRatioBarChart ratio={ratio} risk={riskStatus} />
						</MarginRatioBarRow>
						<InfoBoxRow
							title={t('futures.market.trade.trade-balance.maintenance-margin')}
							textValue={isAccountConnected ? formatDollars(requiredMaintenanceMargin) : NO_VALUE}
						/>
						<InfoBoxRow
							capitalizeLabel={false}
							title={
								provider === PerpsProvider.SNX_V3_BASE
									? t('futures.market.trade.trade-balance.account-equity-with-asset', {
											asset: 'USDC',
										})
									: t('futures.market.trade.trade-balance.account-equity')
							}
							textValue={
								isAccountConnected ? formatDollars(availableMargin, { floor: true }) : NO_VALUE
							}
						/>
					</MarginInfoSection>
				</>
			) : (
				<FlexDivCol>
					<InfoBoxRow
						title={t('futures.market.trade.trade-balance.unrealized-pnl')}
						textValue={getInfoValue(totalUnPnl)}
						color={totalUnPnlColor}
					/>
					<InfoBoxRow
						capitalizeLabel={false}
						title={t('futures.market.trade.trade-balance.account-equity', {
							asset: 'sUSD',
						})}
						textValue={getInfoValue(isolatedAccountEquity)}
					/>
				</FlexDivCol>
			)}
		</Container>
	)
})

const StyledPill = styled(Pill)`
	position: relative;
	text-transform: uppercase;
	height: 16px;
	padding: 2px 6px;
	display: flex;
	align-items: center;
	gap: 4px;
	justify-content: center;
	font-size: 8px;
	border: 1px solid transparent;
	cursor: default;

	${(props) => `
		color: ${props.theme.colors.selectedTheme.newTheme.text.primary};
		background: ${props.theme.colors.selectedTheme.newTheme.pill.gray.hover.background};
		svg {
			filter: none;
		}
		&:hover {
			background: ${props.theme.colors.selectedTheme.newTheme.pill.gray.hover.background};
			border: 1px solid ${props.theme.colors.selectedTheme.border};
		}
	`}
`

const IconContainer = styled(FlexDivCol)`
	svg {
		width: 10px;
		height: 10px;
	}
`

const Container = styled.div<{ mobile?: boolean }>`
	width: 100%;
	padding: 15px 15px;
	border-bottom: ${(props) => (props.mobile ? props.theme.colors.selectedTheme.border : 0)};
`

const MarginInfoSection = styled.div`
	padding-top: 5px;
`

const MarginRatioBarRow = styled.div`
	margin-bottom: 6px;
`

export default TradeBalance
